import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { get } from '../../api'
import { Alert, Button, Empty, Input, Select, Spinner, StatusChip } from '../../components/ui'
import { useDebounced, useLoad } from '../../hooks'
import { addDays, pluralGuests, shortDate, todayKey } from '../../format'
import type { Booking } from '../../types'
import { BookingDetailModal, NewBookingModal } from './BookingModals'
import { useBusiness } from './Layout'

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'confirmed,seated,awaiting_approval', label: 'Upcoming and live' },
  { value: 'awaiting_approval', label: 'Needs approval' },
  { value: 'finished', label: 'Finished' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No-shows' },
]

export function BookingsPage() {
  const { business } = useBusiness()
  const today = todayKey(business.timezone)
  const [params, setParams] = useSearchParams()
  const from = params.get('from') ?? today
  const to = params.get('to') ?? addDays(today, 13)
  const status = params.get('status') ?? ''
  const [q, setQ] = useState(params.get('q') ?? '')
  const query = useDebounced(q)
  const [open, setOpen] = useState<Booking | null>(null)
  const [creating, setCreating] = useState(false)

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k)
    setParams(next, { replace: true })
  }

  const search = new URLSearchParams({ limit: '500' })
  if (query) search.set('q', query)
  else {
    search.set('from', from)
    search.set('to', to)
  }
  if (status) search.set('status', status)
  const list = useLoad(() => get<{ bookings: Booking[] }>(`/v1/admin/businesses/${business.id}/bookings?${search}`), [business.id, from, to, status, query])

  const byDate = new Map<string, Booking[]>()
  for (const b of list.data?.bookings ?? []) byDate.set(b.date, [...(byDate.get(b.date) ?? []), b])

  const exportCsv = () => {
    const rows = [['Date', 'Time', 'Guests', 'Name', 'Phone', 'Email', 'Area', 'Status', 'Source', 'Reference', 'Requests', 'Notes']]
    for (const b of list.data?.bookings ?? []) rows.push([b.date, b.time, String(b.partySize), b.guest.name, b.guest.phone ?? '', b.guest.email ?? '', b.areaName ?? '', b.status, b.source, b.reference, b.specialRequests ?? '', b.internalNotes ?? ''])
    const csv = rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\r\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `bookings-${from}-to-${to}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <div className="topbar">
        <h1>Bookings</h1>
        <div className="row-flex topbar-actions">
          <Button variant="ghost" size="sm" onClick={exportCsv} disabled={!list.data?.bookings.length}>
            Export CSV
          </Button>
          <Button variant="primary" onClick={() => setCreating(true)}>
            + New booking
          </Button>
        </div>
      </div>
      <div className="row-flex" style={{ marginBottom: 14 }}>
        <div className="search grow" style={{ minWidth: 200 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <Input placeholder="Search all bookings by name, phone, email or reference" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search bookings" />
        </div>
        {!query ? (
          <>
            <Input type="date" value={from} onChange={(e) => set({ from: e.target.value })} aria-label="From" style={{ width: 'auto' }} />
            <span className="muted">to</span>
            <Input type="date" value={to} onChange={(e) => set({ to: e.target.value })} aria-label="To" style={{ width: 'auto' }} />
          </>
        ) : null}
        <Select value={status} onChange={(e) => set({ status: e.target.value })} aria-label="Status" style={{ width: 'auto' }}>
          {STATUS_FILTERS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
      </div>

      {list.error ? <Alert>{list.error}</Alert> : null}
      {list.loading && !list.data ? <Spinner /> : null}
      {list.data && list.data.bookings.length === 0 ? <Empty title="Nothing here">{query ? 'No bookings match that search.' : 'No bookings in this date range.'}</Empty> : null}
      <div className="stack-lg">
        {[...byDate.entries()].map(([date, bookings]) => (
          <section key={date}>
            <h3 className="muted" style={{ margin: '0 0 6px 4px' }}>
              {shortDate(date)} · {bookings.length} {bookings.length === 1 ? 'booking' : 'bookings'}, {bookings.filter((b) => b.status === 'confirmed' || b.status === 'seated').reduce((s, b) => s + b.partySize, 0)} covers
            </h3>
            <div className="card table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Guest</th>
                    <th>Party</th>
                    <th>Area</th>
                    <th>Status</th>
                    <th>Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="clickable" onClick={() => setOpen(b)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOpen(b)}>
                      <td className="mono">{b.time}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{b.guest.name}</div>
                        <div className="muted small">{b.guest.phone ?? b.guest.email ?? ''}</div>
                      </td>
                      <td>{pluralGuests(b.partySize)}</td>
                      <td>{b.areaName ?? '—'}</td>
                      <td>
                        <StatusChip status={b.status} />
                      </td>
                      <td className="mono muted">{b.reference}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
      <Button variant="primary" className="fab" onClick={() => setCreating(true)} aria-label="New booking">
        + New
      </Button>
      {open ? (
        <BookingDetailModal
          booking={open}
          onClose={() => setOpen(null)}
          onChange={(updated) => {
            setOpen(updated)
            void list.reload({ quiet: true })
          }}
        />
      ) : null}
      {creating ? (
        <NewBookingModal
          initialDate={today}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            void list.reload({ quiet: true })
          }}
        />
      ) : null}
    </>
  )
}
